import {defineStore} from "pinia";
import * as DateUtils from "@/helpers/dateUtils";
import {Yacht} from "@/models/Yacht";
import {Offer} from "@/models/Offer";
import {computed, ref, watch} from "vue";
import {useFormatStore} from "@/stores/formatStore";
import {ILocation} from "@/models/Location";
import {useAmenityPricing} from "@/composables/useAmenityPricing";


export const useOfferStore = defineStore('offers', () => {

    const formatStore = useFormatStore();
    const state = ref('idle');
    const booked = ref(false);
    const slug = ref('');
    const location = ref<ILocation>()
    const equipmentShown = ref(5);
    const equipmentShow = ref(false);
    const yacht = ref<Partial<Yacht>>()
    const selections = ref({
        dateFrom: new Date(),
        dateTo: new Date(),
        persons: 0
    })

    const dateDays = ref(1)
    const availabilities = ref([])
    const selectedAmenities = ref([])

    const offer = computed((): Offer => null)
    
    const yachtLocation = computed({
        get() {
            const availability = (availabilities.value[1] && availabilities.value[1].location) || {};
            return {
                name: availability.name,
                lat: availability.latitude || 0,
                lng: availability.longitude || 0,
                country: availability.country
            };
        },
        set(newValue) {
            if (!availabilities.value[1]) {
                return;
            }

            if (!availabilities.value[1].location) {
                availabilities.value[1].location = {};
            }

            availabilities.value[1].location.name = newValue.name;
            availabilities.value[1].location.latitude = newValue.lat;
            availabilities.value[1].location.longitude = newValue.lng;

            console.log("Updated location", availabilities.value[1].location);
        }
    });


    const mandatoryAmenities = computed(() => {
        const amenities = availabilities.value?.[1]?.amenities || [];
        return amenities.filter(a => a.basic === 'composite' && a.obligatory);
    });

    const optionalAmenities = computed(() => {
        const amenities = availabilities.value?.[1]?.amenities || [];
        return amenities.filter(a => a.basic === 'composite' && !a.obligatory);
    });

    const selectedAvailabilityDatesText = computed(() => {
        return `${formatStore.formatDate(selections.value.dateFrom)} – ${formatStore.formatDate(selections.value.dateTo)}`
    })

    const equipment = computed(() => {
        let amenities = []
        if (availabilities.value && availabilities.value.length > 2) {
            amenities = availabilities.value[1].amenities
        }

        if (amenities.length <= equipmentShown.value) {
            return {
                'show': amenities,
                'hide': [],
                'hidden': 0,
                'total': amenities.length,
            }
        }

        const equipment = amenities.filter((amenity) => amenity.basic == 'basic')
        return {
            'show': equipment.slice(0, equipmentShown.value),
            'hide': equipment.slice(equipmentShown.value, equipment.length),
            'hidden': equipment.length - equipmentShown.value,
            'total': equipment.length,
        }
    })

    const basePrice = computed(() => availabilities.value[1]?.basePrice || 0);
    const offerPrice = computed(() => availabilities.value[1]?.offerPrice || 0);


    const { priceDetails } = useAmenityPricing({
        basePrice: computed(() => basePrice.value || 0),
        offerPrice: computed(() => offerPrice.value || 0),
        mandatoryAmenities: mandatoryAmenities,
        optionalAmenities: optionalAmenities,
        selectedAmenities: computed(() => selectedAmenities.value.map(a => a.id)),
        selections: selections,
        dateDays: dateDays
    });

    const passengers = computed(() => {
        return [...Array(Number(yacht.value.berths)).keys()].map(i => i + 1).filter(i => i <= yacht.value.berths)
    })

    
    const setFromOffer = (offer: any) => {
        
        // Dates & passengers
        const toDate = (d: any) => (d instanceof Date ? d : (d ? new Date(d) : new Date()));
        const dateFrom = toDate(offer?.dateFrom ?? offer?.dateStart);
        const dateTo   = toDate(offer?.dateTo   ?? offer?.dateEnd);
        selections.value.dateFrom = dateFrom;
        selections.value.dateTo   = dateTo;
        selections.value.persons  = Number(offer?.passengers ?? selections.value.persons ?? 0);
        dateDays.value = DateUtils.computeDaysBetween(dateFrom, dateTo);

        // Core yacht & location 
        yacht.value = offer?.yacht || {};
        const loc = offer?.locationFrom || offer?.yacht?.location || null;

        // Amenities
        const allAmenities = Array.isArray(offer?.yacht?.amenities) ? offer.yacht.amenities : [];
      
        const selected = Array.isArray(offer?.selectedAmenities)
            ? offer.selectedAmenities.map(a => {
                if (typeof a === 'object') return { ...a, id: Number(a.id) };
                const found = allAmenities.find(am => Number(am.id) === Number(a));
                return found ? { ...found, id: Number(found.id) } : null;
            }).filter(Boolean)
            : [];
        selectedAmenities.value = selected;
        // Build the three availability slots to satisfy existing computed props
        const prevPeriod = DateUtils.previousPeriods(dateFrom, dateTo)[0];
        const nextPeriod = DateUtils.nextPeriods(dateFrom, dateTo, 1)[0];

        const prev = {
            basePrice: 0.0,
            offerPrice: 0.0,
            available: false,
            location: false,
            amenities: [],
            state: 'idle',
            ...prevPeriod,
        };

        const selectedSlot = {
            basePrice: Number(offer?.price ?? 0),
            offerPrice: Number(offer?.discountedPrice ?? offer?.price ?? 0),
            discount: Number(offer?.discount ?? 0),
            available: Boolean(offer?.free ?? true),
            location: loc
                ? {
                    id: loc.id,
                    name: loc.name,
                    latitude: loc.latitude ?? 0,
                    longitude: loc.longitude ?? 0,
                    country: loc.country?.name || loc.country || undefined,
                }
                : false,
            amenities: allAmenities,
            state: 'idle',
            dateFrom,
            dateTo,
        };

        const next = {
            basePrice: 0.0,
            offerPrice: 0.0,
            location: 0.0,
            amenities: [],
            available: false,
            state: 'idle',
            ...nextPeriod,
        };

        availabilities.value = [prev, selectedSlot, next];
    };

    
    const resetState = ()=>{
        console.log("Inside reset state")
        state.value = 'idle';
        yacht.value = undefined;
        // selections.value = {
        //     dateFrom: new Date(),
        //     dateTo: new Date(),
        //     persons: 0
        // }
        selectedAmenities.value = [];
    }

    return {

        state,
        booked,
        slug,
        location,
        equipmentShown,
        equipmentShow,
        yacht,
        selections,
        dateDays,
        availabilities,
        selectedAmenities,
        offer,
        mandatoryAmenities,
        optionalAmenities,
        equipment,
        priceDetails,
        selectedAvailabilityDatesText,
        yachtLocation,
        setFromOffer,
        passengers,
        resetState,
    }


});
